import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Utility functions for performance optimization

// Debounce function to limit how often a function can be called
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function to limit function execution rate
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Preload image for better perceived performance
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
};

// Preload multiple images
export const preloadImages = (srcs: string[]): Promise<void[]> => {
  return Promise.all(srcs.map(src => preloadImage(src)));
};

// Add loading delay to prevent flash of loading states
export const addLoadingDelay = (minDelay: number = 300): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, minDelay));
};

// Check if element is in viewport for lazy loading
export const isInViewport = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

// Intersection Observer for lazy loading
export const createIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options: IntersectionObserverInit = {}
): IntersectionObserver => {
  return new IntersectionObserver(callback, {
    threshold: 0.1,
    rootMargin: '50px',
    ...options,
  });
};

// Performance measurement utility
export const measurePerformance = <T extends (...args: any[]) => any>(
  name: string,
  fn: T,
  ...args: Parameters<T>
): ReturnType<T> => {
  const start = performance.now();
  const result = fn(...args);
  const end = performance.now();

  if (process.env.NODE_ENV === 'development') {
    console.log(`${name} took ${end - start}ms`);
  }

  return result;
};

// Async performance measurement
export const measureAsyncPerformance = async <T extends (...args: any[]) => Promise<any>>(
  name: string,
  fn: T,
  ...args: Parameters<T>
): Promise<ReturnType<T>> => {
  const start = performance.now();
  const result = await fn(...args);
  const end = performance.now();

  if (process.env.NODE_ENV === 'development') {
    console.log(`${name} took ${end - start}ms`);
  }

  return result;
};
